import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";
import { KokeileTanaan, MiksiToimii, ValmisRatkaisu } from "@/components/hub/Callouts";

const SLUG = "montessori-kotona";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Montessori kotona — käytännön opas suomalaisille vanhemmille";
const DESC =
  "Mistä aloittaa Montessori kotona: eteinen, keittiö, lastenhuone, kylpyhuone, lelut ja arjen tehtävät huone kerrallaan — ja mitä ei tarvitse ostaa lainkaan.";
const PUBLISHED = "2026-08-16";
const HERO = articleHeroFor(SLUG);

export const Route = createFileRoute("/montessori-koulu/montessori-kotona")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      eyebrow="Käytännön opas"
      title={TITLE}
      lead="Montessori-ajattelun soveltaminen kotona ei vaadi suurta määrää erityisiä välineitä. Useimmat muutokset syntyvät järjestelemällä sitä, mitä kotona jo on: tavarat lapsen ulottuville, valikoima pienemmäksi ja lapselle oma paikka jokaisessa arjen tilanteessa."
      readingMinutes={11}
      publishedISO={PUBLISHED}
      updatedISO={PUBLISHED}
    >
      <h2>Mistä aloittaa</h2>
      <p>Aloita siitä huoneesta, jossa arki takkuaa eniten. Monelle perheelle se on eteinen tai keittiö: lapsi haluaa osallistua, mutta ei yllä tarvitsemiinsa tavaroihin, jolloin aikuinen tekee kaiken puolesta ja molemmat turhautuvat.</p>
      <p>Yksi muutos kerrallaan riittää. Kokeile viikko, katso mitä lapsi tekee, ja säädä sen mukaan. Koko kodin järjestäminen yhtenä viikonloppuna johtaa yleensä siihen, että järjestys purkautuu parissa viikossa.</p>

      <KokeileTanaan>
        Valitse yksi tilanne, jossa lapsi pyytää apua joka päivä. Muuta siitä yksi asia niin, että lapsi yltää itse — esimerkiksi siirrä vesilasit alimmalle hyllylle.
      </KokeileTanaan>

      <MiksiToimii>
        Kun lapsi pystyy tavoittamaan tarvitsemansa tavarat itse, hänen ei tarvitse pyytää aikuista jokaiseen pieneen tehtävään. Tämä voi antaa enemmän mahdollisuuksia harjoitella itsenäistä toimintaa arjessa.
      </MiksiToimii>

      <h2>Eteinen</h2>
      <p>Eteinen on usein nopein voitto, koska siellä toistuu sama tilanne kahdesti päivässä.</p>
      <ul>
        <li>Yksi koukku lapsen korkeudella takkia varten — ei koko naulakkoa uusiksi.</li>
        <li>Matala kori tai hylly kengille, jotta lapsi löytää ja palauttaa ne itse.</li>
        <li>Paikka istua kenkiä puettaessa: pieni jakkara tai laatikko riittää.</li>
        <li>Peili lapsen korkeudella, jos tilaa on — auttaa tarkistamaan hatun ja huivin itse.</li>
      </ul>

      <h2>Keittiö</h2>
      <p>Keittiössä lapsi haluaa yleensä olla mukana siellä, missä aikuinen työskentelee. Kaksi muutosta riittää alkuun: lapselle oma ulottuvilla oleva taso ja oma paikka, josta hän löytää omat astiansa.</p>
      <ul>
        <li>Oma laatikko tai kaapin alahylly: lasi, lautanen, ruokailuvälineet ja pieni kannu.</li>
        <li>Pieni pyyhe näkyvällä paikalla, jotta läikkyneen voi pyyhkiä itse.</li>
        <li>Yksi työtehtävä kerrallaan: vihannesten pesu, salaatin repiminen, pöydän kattaminen.</li>
      </ul>
      <p>Jos lapsi ei yllä työtasolle, tukeva koroke tai oppimistorni voi tehdä osallistumisesta mahdollista. Tarkista aina tuotekohtainen ikäsuositus ja valmistajan käyttöohjeet, ja käytä tornia aikuisen valvonnassa.</p>

      <h2>Lastenhuone</h2>
      <p>Tavoite ei ole tietynlainen sisustus vaan huone, jossa lapsi löytää tavaransa ja osaa laittaa ne takaisin.</p>
      <ul>
        <li>Matala avohylly, jossa on rajattu määrä leluja esillä kerrallaan.</li>
        <li>Jokaisella tavaralla oma paikka — kori, tarjotin tai merkitty lokero.</li>
        <li>Kirjat kannet näkyvissä, jotta lapsi voi valita itse.</li>
        <li>Vapaata lattiatilaa leikkiin; sitä tarvitaan enemmän kuin uusia kalusteita.</li>
      </ul>

      <h2>Kylpyhuone</h2>
      <ul>
        <li>Tukeva koroke lavuaarin ääreen, jos lapsi ei yllä hanalle.</li>
        <li>Oma pyyhe omalla koukullaan lapsen korkeudella.</li>
        <li>Hammasharja ja tahna paikassa, josta lapsi ottaa ne itse.</li>
      </ul>

      <h2>Lelut ja niiden kierto</h2>
      <p>Kun kaikki lelut ovat esillä yhtä aikaa, moni lapsi selaa niitä eikä syvenny mihinkään. Rajattu valikoima helpottaa valitsemista ja tekee siivoamisesta lapselle mahdollisen.</p>
      <ul>
        <li>Pidä esillä pieni määrä leluja; loput laatikkoon säilöön.</li>
        <li>Vaihda valikoimaa silloin, kun kiinnostus hiipuu — ei kalenterin mukaan.</li>
        <li>Jätä esille se, mitä lapsi käyttää juuri nyt, vaikka se toistuisi viikkoja.</li>
      </ul>

      <KokeileTanaan title="Pieni muutos kotiin">
        Siirrä kolme lapsen eniten käyttämää kirjaa alimmalle hyllylle niin, että hän voi itse valita ja palauttaa ne.
      </KokeileTanaan>

      <h2>Arjen tehtävät ja itsenäisyys</h2>
      <p>Käytännön elämän tehtävät ovat Montessorin ydintä: ne ovat aitoja, niillä on näkyvä lopputulos ja lapsi tekee niitä yhdessä perheen kanssa. Pöydän kattaminen, pyykin lajittelu, kasvien kastelu ja pölyjen pyyhkiminen sopivat useimpiin koteihin ilman erityisvälineitä.</p>
      <p>Anna tehtävälle enemmän aikaa kuin itse käyttäisit, ja kuvaile mieluummin kuin korjaa. Keskeneräinenkin suoritus on harjoitusta.</p>

      <h2>Turvallisuus</h2>
      <p>Kun lapsi liikkuu ja kurottaa itsenäisemmin, ympäristön turvallisuus kannattaa käydä läpi samalla: huonekalujen kiinnitys seinään, johdot, pienet irtoesineet ja portaat. Kalusteiden ja välineiden osalta noudata aina valmistajan omia ohjeita ja tuotekohtaisia ikäsuosituksia — ne menevät yleisten neuvojen edelle.</p>

      <h2>Mitä ei tarvitse ostaa</h2>
      <p>Montessori ei ole ostoslista. Ilman uusia hankintoja pääsee pitkälle:</p>
      <ul>
        <li>Kori tai pahvilaatikko toimii säilytyksenä yhtä hyvin kuin muotoiltu hylly.</li>
        <li>Tavallinen pieni kannu ja lasi riittävät kaatamisen harjoitteluun.</li>
        <li>Yksi koukku sopivalla korkeudella korvaa kokonaisen lasten naulakon.</li>
        <li>Leluja ei tarvitse vaihtaa puisiin — määrän rajaaminen vaikuttaa enemmän kuin materiaali.</li>
      </ul>

      <ValmisRatkaisu
        intro="Osa muutoksista on helpompi toteuttaa valmiilla kalusteella. Nämä ovat aiheeseen suoraan liittyvät vaihtoehdot — tarkista tuotekohtainen ikäsuositus ja valmistajan ohjeet ennen käyttöä."
        picks={[
          { to: "/oppimistorni", label: "Tutustu oppimistorneihin", note: "Nostaa lapsen työtason korkeudelle keittiössä, aikuisen valvonnassa." },
          { to: "/lasten-vaaterekki", label: "Tutustu vaaterekkeihin", note: "Vaatteet lapsen ulottuville, kun eteisen naulakko on liian korkealla." },
          { to: "/lastenhuoneen-kalusteet", label: "Tutustu lastenhuoneen kalusteisiin", note: "Matalat hyllyt ja säilytys, jossa tavaroilla on oma paikka." },
        ]}
      />

      <h2>Jatka lukemista</h2>
      <p>
        Sarjan seuraavat osat syventävät yksittäisiä huoneita ja tilanteita:{" "}
        <Link to="/montessori-koulu/montessori-kalusteet-kotiin">Montessori-kalusteet kotiin</Link>,{" "}
        <Link to="/montessori-koulu/pienet-muutokset-kotona-itsenaisyys">pienet muutokset kotona</Link> ja{" "}
        <Link to="/montessori-koulu/lapsi-keittiossa-ikaopas">lapsi keittiössä</Link>.
      </p>
    </BlogPost>
  );
}